import { normalizeProductRouteValue } from './navigation'
import type { Product } from './types'

/**
 * Confirma se o payload que está em memória ainda pertence à rota de detalhe
 * atual. A listagem pode carregar o produto pai enquanto a URL aponta para uma
 * variação, por isso aceitamos tanto o ID da rota quanto o contexto do pai.
 */
export const productMatchesDetailRoute = (
  product: Pick<
    Product,
    'id' | 'parentProductId' | 'navigationTargetProductId' | 'navigationParentContextId'
  > | null | undefined,
  routeProductId?: string | null,
  parentContextId?: string | null,
) => {
  if (!product) return false

  const routeId = normalizeProductRouteValue(routeProductId)
  const parentId = normalizeProductRouteValue(parentContextId)
  if (!routeId && !parentId) return false

  const productIds = [
    product.id,
    product.parentProductId,
    product.navigationTargetProductId,
    product.navigationParentContextId,
  ]
    .map((value) => normalizeProductRouteValue(value))
    .filter((value): value is string => Boolean(value))

  return productIds.includes(routeId ?? '') || productIds.includes(parentId ?? '')
}

/**
 * Permite manter o detalhe montado quando a URL ganha o contexto `?pai=` da
 * mesma família. A própria tela de detalhe já carrega e troca as variações;
 * buscar o pai novamente nesse momento desmonta a tela e causa um flash.
 */
export const productSupportsDetailParentContext = (
  product: Pick<Product, 'id' | 'parentProductId'> | null | undefined,
  parentContextId?: string | null,
) => {
  if (!product) return false

  const parentId = normalizeProductRouteValue(parentContextId)
  if (!parentId) return false

  return normalizeProductRouteValue(product.id) === parentId
    || normalizeProductRouteValue(product.parentProductId) === parentId
}

/**
 * Produtos com variações, mas sem seletor visual, abrem uma SKU concreta no
 * topo do detalhe. A rota tem preferência; ao entrar pelo card do produto-pai,
 * usamos a primeira variação já devolvida pelo lote da família.
 */
export const resolveDirectDisplayVariation = <T extends Pick<Product, 'id' | 'parentProductId'>>(
  product: Pick<Product, 'id' | 'parentProductId' | 'hasForm'>,
  variations: T[],
  routeProductId?: string | null,
  selectorEnabled = false,
): T | null => {
  if (selectorEnabled || product.hasForm === true || variations.length === 0) return null

  const routeId = normalizeProductRouteValue(routeProductId)
  const routeVariation = routeId
    ? variations.find((variation) => normalizeProductRouteValue(variation.id) === routeId)
    : undefined
  if (routeVariation) return routeVariation

  if (product.parentProductId) {
    const currentVariation = variations.find((variation) => (
      normalizeProductRouteValue(variation.id) === normalizeProductRouteValue(product.id)
    ))
    if (currentVariation) return currentVariation
  }

  return variations[0] ?? null
}
